import { Background } from '@app/core/utils/background';
import {
  BACKEND_LOGIN_ATTEMPT_KEY,
  LoginEntrypointParams,
  SESSION_EXPIRED_KEY,
  SessionService,
} from '../session.service';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { LoggerService } from '@app/core/services/logger.service';
import {
  ServiceNotice,
  ServiceNoticeService,
} from '@app/core/services/service-notice.service';
import { User } from '@app/core/models/user';
import { ActionService } from '@app/cx-builder/message-builder/services/action.service';
import { NgClass } from '@angular/common';
import { ErrorDisplayComponent } from '../../shared/error-display/error-display.component';
import { SpinnerComponent } from '../../shared/spinner/spinner.component';

// DIS-1781: why an Auth0 login could not complete. The backend's
// /auth/callback sends ?auth_error=<code>; Auth0's tenant error page (see
// rn-auth0-infra auth0_tenant.tf) sends ?error=...&error_description=...
// Only these fixed codes select copy -- neither param's text is ever rendered,
// since anyone can put arbitrary text in a /login URL.
export type LoginAuthError = 'invitation_invalid' | 'login_failed';

const LOGIN_AUTH_ERRORS: ReadonlySet<string> = new Set<LoginAuthError>([
  'invitation_invalid',
  'login_failed',
]);

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  standalone: true,
  imports: [
    FormsModule,
    NgClass,
    ReactiveFormsModule,
    ErrorDisplayComponent,
    RouterLink,
    SpinnerComponent,
  ],
})
export class LoginComponent implements OnInit, OnDestroy {
  email_address: string;
  password: string = '';
  now: Date;
  loginFormGroup: UntypedFormGroup;
  mfaFormGroup: UntypedFormGroup;
  loginInProgress = false;
  error: string;
  requestInFlight: boolean;

  ssoCode: string;
  ssoScreen: boolean;
  ssoError: string;
  clientId: string;

  qrCode: string;
  isTotp: boolean;
  loginUiReady = false;
  authError: LoginAuthError | null = null;
  // DIS-1789: the session ended without the user asking (see
  // SESSION_EXPIRED_KEY). Shows a sign-in prompt instead of redirecting.
  sessionExpired = false;
  // DIS-1767: an outage or maintenance notice shown before sign-in starts. The
  // user continues to sign in from it; it never blocks them.
  serviceNotice: ServiceNotice | null = null;
  continuingToSignIn = false;
  private noticeSub: Subscription;

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private sessionService: SessionService,
    private formBuilder: UntypedFormBuilder,
    protected actionService: ActionService,
    private serviceNoticeService: ServiceNoticeService,
  ) {
    // Apply login background and initial UI path before first render to avoid visible repaints.
    Background.loginOn();
    this.loginUiReady = this.shouldRenderImmediately();
  }

  ngOnInit() {
    this.loginFormGroup = this.formBuilder.group({
      email_address: [null, [Validators.required]],
      password: [null, Validators.required],
    });
    this.mfaFormGroup = this.formBuilder.group({
      mfaCode: ['', Validators.required],
    });
    // Read before reset(), which clears sessionStorage. Single-use: the next
    // visit to /login behaves normally.
    const sessionExpired = sessionStorage.getItem(SESSION_EXPIRED_KEY) === '1';
    this.reset();
    this.now = new Date();
    this.ssoCode = this.activatedRoute.snapshot.queryParams['code'];
    this.clientId = this.activatedRoute.snapshot.params['clientId'];

    if (this.ssoCode) {
      this.clearBackendLoginAttempt();
      this.loginUiReady = true;
      this.ssoLogin();
      return;
    }

    // Show the message instead of bouncing straight back to Auth0, which
    // would just hand the user a login page with no explanation.
    this.authError = this.readAuthError();
    if (this.authError) {
      this.clearBackendLoginAttempt();
      this.loginUiReady = true;
      return;
    }

    // Wait for the user rather than opening Auth0's page now: they may be
    // away, and the login's single-use cookies expire while it sits open.
    if (sessionExpired) {
      this.sessionExpired = true;
      this.clearBackendLoginAttempt();
      this.loginUiReady = true;
      return;
    }

    if (this.shouldFallbackToLegacyLogin()) {
      this.loginUiReady = true;
      return;
    }

    this.startBackendLogin();
  }

  ngOnDestroy() {
    this.noticeSub?.unsubscribe();
    Background.loginOff();
  }

  get mfaCodeFormControl(): UntypedFormControl {
    return <UntypedFormControl>this.mfaFormGroup.controls['mfaCode'];
  }
  attemptVerify() {
    this.mfaFormGroup.markAllAsTouched();
    if (this.mfaFormGroup.valid && !this.requestInFlight) {
      this.requestInFlight = true;

      this.sessionService
        .verifyMFACode(this.mfaCodeFormControl.value)
        .subscribe({
          next: (resp) => {
            this.requestInFlight = false;
            this.router.navigateByUrl('/safe-redirect');
          },
          error: (err) => {
            this.requestInFlight = false;
            this.mfaCodeFormControl.setErrors({
              customErrorMessage: 'Incorrect code. Please try again.',
            });
          },
        });
    }
  }

  attemptLogin() {
    const controls = this.loginFormGroup.controls;
    for (const control in controls) {
      if (controls.hasOwnProperty(control)) {
        controls[control].markAsTouched();
      }
    }

    if (this.loginFormGroup.valid && !this.loginInProgress) {
      this.requestInFlight = true;
      this.error = undefined;
      this.loginInProgress = true;
      this.sessionService
        .login(
          this.loginFormGroup.controls['email_address'].value,
          this.loginFormGroup.controls['password'].value,
        )
        .subscribe(
          (loginResp) => {
            this.loginInProgress = false;
            this.requestInFlight = false;
            if (loginResp instanceof User) {
              this.router.navigateByUrl('/safe-redirect');
            } else {
              this.qrCode = loginResp['qr'] ?? '';
              this.isTotp = loginResp['totp'] ?? false;
            }
          },
          (err) => {
            this.parseError(err);
          },
        );
    }
  }

  // "Go to sign in" / "Try again" from the auth error message: a normal
  // backend login, without the query params that produced the error.
  retryLogin(): void {
    this.markBackendLoginRedirectAttempt();
    this.sessionService.redirectToBackendLogin({});
  }

  // "Sign in" from the session-expired prompt: the same backend login the page
  // would otherwise have started on load, keeping any returnUrl.
  continueLogin(): void {
    this.startBackendLogin();
  }

  // "Continue to sign in" from a service notice: the user has read it, so go
  // straight to the backend login without checking for a notice again.
  // The notice stays on screen while the page navigates away: clearing it would
  // flash the legacy sign-in form, and a second click must not start a second
  // redirect.
  continueAfterNotice(): void {
    if (this.continuingToSignIn) {
      return;
    }
    this.continuingToSignIn = true;
    this.markBackendLoginRedirectAttempt();
    this.redirectToBackendLoginEntrypoint();
  }

  checkForEnter(event) {
    if (event.keyCode === 13) {
      this.attemptLogin();
    }
  }

  private parseError(err: any) {
    this.loginInProgress = false;
    this.requestInFlight = false;
    const defaultError =
      'The email address and password were not correct. Try again.';
    if (err && err.error && err.error.reason) {
      this.error = err.error.reason;
    } else {
      this.error = defaultError;
    }
  }

  private reset(): void {
    const backendLoginRedirectAttempt = sessionStorage.getItem(
      BACKEND_LOGIN_ATTEMPT_KEY,
    );

    localStorage.clear();
    sessionStorage.clear();

    if (backendLoginRedirectAttempt === '1') {
      sessionStorage.setItem(
        BACKEND_LOGIN_ATTEMPT_KEY,
        backendLoginRedirectAttempt,
      );
    }
  }

  private shouldRenderImmediately(): boolean {
    const queryParams = this.activatedRoute.snapshot.queryParams || {};
    const hasSsoCode = !!queryParams['code'];
    const attemptedBackendRedirect =
      sessionStorage.getItem(BACKEND_LOGIN_ATTEMPT_KEY) === '1';

    const sessionExpired = sessionStorage.getItem(SESSION_EXPIRED_KEY) === '1';

    return (
      hasSsoCode ||
      attemptedBackendRedirect ||
      sessionExpired ||
      !!this.readAuthError()
    );
  }

  private readAuthError(): LoginAuthError | null {
    const queryParams = this.activatedRoute.snapshot.queryParams || {};

    const authError = queryParams['auth_error'];
    if (typeof authError === 'string' && LOGIN_AUTH_ERRORS.has(authError)) {
      return authError as LoginAuthError;
    }

    const error = queryParams['error'];
    if (typeof error === 'string' && error.length > 0) {
      const description = queryParams['error_description'];
      return typeof description === 'string' && /invitation/i.test(description)
        ? 'invitation_invalid'
        : 'login_failed';
    }

    return null;
  }

  private shouldFallbackToLegacyLogin(): boolean {
    const attemptedBackendRedirect =
      sessionStorage.getItem(BACKEND_LOGIN_ATTEMPT_KEY) === '1';

    if (attemptedBackendRedirect) {
      this.clearBackendLoginAttempt();
    }

    return attemptedBackendRedirect;
  }

  private markBackendLoginRedirectAttempt(): void {
    sessionStorage.setItem(BACKEND_LOGIN_ATTEMPT_KEY, '1');
  }

  private clearBackendLoginAttempt(): void {
    sessionStorage.removeItem(BACKEND_LOGIN_ATTEMPT_KEY);
  }

  // Starts the backend login, unless there is a service notice to show first.
  // The check cannot fail or stall the login: the service answers "no notice"
  // on any error or after a short timeout. The marker is only set once the
  // redirect really starts, so a notice the user sits on does not make the
  // next visit fall back to the legacy form.
  private startBackendLogin(): void {
    this.noticeSub?.unsubscribe();
    this.noticeSub = this.serviceNoticeService
      .getActiveNotice()
      .subscribe((notice) => {
        if (notice) {
          this.sessionExpired = false;
          this.serviceNotice = notice;
          this.loginUiReady = true;
          return;
        }
        this.sessionExpired = false;
        this.markBackendLoginRedirectAttempt();
        this.redirectToBackendLoginEntrypoint();
      });
  }

  private redirectToBackendLoginEntrypoint(): void {
    const queryParams = this.activatedRoute.snapshot.queryParams || {};
    const params: LoginEntrypointParams = {
      returnUrl: queryParams['returnUrl'],
      invitation: queryParams['invitation'],
      organization: queryParams['organization'],
      organization_name: queryParams['organization_name'],
    };
    this.sessionService.redirectToBackendLogin(params);
  }

  private ssoLogin(): void {
    this.ssoError = null;
    this.ssoScreen = true;

    this.sessionService.ssoLogin(this.ssoCode, this.clientId).subscribe({
      next: (user) => {
        this.ssoScreen = false;
        this.router.navigateByUrl('/safe-redirect');
      },
      error: (err) => {
        LoggerService.log('LoginComponent', err);
        this.ssoError = err?.error?.reason || 'there was an error';
      },
    });
  }
}
